import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Clock, XCircle, Upload, Loader2 } from "lucide-react";
import { api, formatApiError, fileUrl } from "@/lib/api";

const inputCls = "w-full border border-slate-200 bg-[#FFFFFF]/60 px-4 py-3 text-sm text-slate-800 placeholder-slate-400 outline-none transition-colors focus:border-[#6D28D9]/60";
const ID_TYPES = ["Aadhaar", "Passport", "Voter ID", "Driving Licence"];
const EMPTY = { pan: "", id_type: "Aadhaar", id_number: "", id_doc_path: "", pan_doc_path: "", demat_id: "" };

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-slate-500">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);

export const KycStatusBadge = ({ status }) => {
  const map = {
    verified: ["border-emerald-600/40 bg-emerald-600/[0.06] text-emerald-700", ShieldCheck, "KYC verified"],
    submitted: ["border-[#6D28D9]/50 bg-[#6D28D9]/[0.08] text-[#6D28D9]", Clock, "KYC under review"],
    rejected: ["border-rose-600/40 bg-rose-600/[0.06] text-rose-700", XCircle, "KYC rejected"],
    none: ["border-slate-300 text-slate-500", Clock, "KYC not started"],
  };
  const [cls, Icon, label] = map[status] || map.none;
  return <span data-testid="kyc-status" className={`inline-flex items-center gap-1.5 border px-3 py-1.5 font-mono2 text-[10px] uppercase tracking-[0.18em] ${cls}`}><Icon className="h-3.5 w-3.5" /> {label}</span>;
};

export const KycForm = ({ onStatus }) => {
  const [kyc, setKyc] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/kyc").then((r) => { setKyc(r.data); onStatus?.(r.data.status); }).catch(() => setKyc({ status: "none" }));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const upload = (key) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/kyc/upload", fd);
      setForm((f) => ({ ...f, [key]: res.data.path }));
      toast.success(key === "pan_doc_path" ? "PAN card uploaded." : "ID document uploaded.");
    } catch (err) { toast.error(formatApiError(err)); } finally { setUploading(""); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.id_doc_path) { toast.error("Please upload a photo of your Aadhaar / ID document."); return; }
    if (!form.pan_doc_path) { toast.error("Please upload a photo of your PAN card."); return; }
    const demat = form.demat_id.replace(/\s/g, "").toUpperCase();
    if (!/^(\d{16}|IN\d{14})$/.test(demat)) { toast.error("Demat account number must be 16 digits (CDSL) or IN + 14 digits (NSDL)."); return; }
    setSaving(true);
    try {
      const res = await api.post("/kyc", { ...form, demat_id: demat });
      setKyc(res.data); onStatus?.(res.data.status);
      toast.success("KYC submitted — our desk will verify it shortly.");
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  if (!kyc) return <p className="mt-6 font-mono2 text-xs uppercase tracking-[0.25em] text-slate-500">Loading KYC…</p>;

  const showForm = kyc.status === "none" || kyc.status === "rejected";

  return (
    <div data-testid="kyc-panel" className="mt-6">
      <div className="flex flex-wrap items-center justify-between gap-4 border border-slate-200 bg-[#F6F4FD]/70 p-6">
        <div>
          <KycStatusBadge status={kyc.status} />
          <p className="mt-3 max-w-xl text-sm text-slate-500">
            {kyc.status === "verified" && "You're fully verified. Buy any share on the board in three simple steps."}
            {kyc.status === "submitted" && "Thanks — your details are with the desk. You can already place orders; the desk confirms your KYC alongside your first transfer."}
            {kyc.status === "rejected" && <>Your KYC was not approved{kyc.note ? <>: <span className="text-rose-700">{kyc.note}</span></> : "."} Please re-submit below.</>}
            {kyc.status === "none" && "SEBI-compliant off-market transfers need your PAN card, Aadhaar (or another government ID) and your 16-digit demat account number. Takes about two minutes."}
          </p>
        </div>
        {kyc.status !== "none" && (
          <dl className="grid grid-cols-2 gap-x-8 gap-y-2 font-mono2 text-[11px] text-slate-500">
            <dt className="text-slate-400">PAN</dt><dd data-testid="kyc-pan-masked">{kyc.pan}</dd>
            <dt className="text-slate-400">{kyc.id_type}</dt><dd>{kyc.id_number}</dd>
            <dt className="text-slate-400">Demat</dt><dd data-testid="kyc-demat-masked">{kyc.demat_id || "—"}</dd>
          </dl>
        )}
      </div>

      {showForm && (
        <form data-testid="kyc-form" onSubmit={submit} className="mt-6 grid gap-6 border border-slate-200 bg-[#F6F4FD]/70 p-6 md:grid-cols-2">
          <div className="md:col-span-2"><p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#6D28D9]/90">1 · PAN card</p></div>
          <Field label="PAN number"><input data-testid="kyc-pan-input" required value={form.pan} onChange={set("pan")} placeholder="ABCDE1234F" maxLength={10} className={`${inputCls} uppercase`} /></Field>
          <Field label="PAN card photo (JPG/PNG, ≤5 MB)">
            <div className="flex items-center gap-3">
              <label data-testid="kyc-pan-upload-label" className="flex cursor-pointer items-center gap-2 border border-[#6D28D9]/40 px-4 py-3 font-mono2 text-[10px] uppercase tracking-[0.18em] text-[#6D28D9] transition-colors hover:bg-[#6D28D9]/10">
                {uploading === "pan_doc_path" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} {form.pan_doc_path ? "Replace" : "Upload"}
                <input data-testid="kyc-pan-upload-input" type="file" accept="image/*" className="hidden" onChange={upload("pan_doc_path")} />
              </label>
              {form.pan_doc_path && <img data-testid="kyc-pan-doc-preview" src={fileUrl(form.pan_doc_path)} alt="PAN preview" className="h-11 w-16 border border-slate-200 object-cover" />}
            </div>
          </Field>
          <div className="md:col-span-2 mt-2"><p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#6D28D9]/90">2 · Aadhaar / government ID</p></div>
          <Field label="ID type">
            <select data-testid="kyc-id-type-select" value={form.id_type} onChange={set("id_type")} className={inputCls}>{ID_TYPES.map((t) => <option key={t}>{t}</option>)}</select>
          </Field>
          <Field label="ID number"><input data-testid="kyc-id-number-input" required value={form.id_number} onChange={set("id_number")} placeholder="ID number as printed" className={inputCls} /></Field>
          <Field label="ID document photo (JPG/PNG, ≤5 MB)">
            <div className="flex items-center gap-3">
              <label data-testid="kyc-upload-label" className="flex cursor-pointer items-center gap-2 border border-[#6D28D9]/40 px-4 py-3 font-mono2 text-[10px] uppercase tracking-[0.18em] text-[#6D28D9] transition-colors hover:bg-[#6D28D9]/10">
                {uploading === "id_doc_path" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} {form.id_doc_path ? "Replace" : "Upload"}
                <input data-testid="kyc-upload-input" type="file" accept="image/*" className="hidden" onChange={upload("id_doc_path")} />
              </label>
              {form.id_doc_path && <img data-testid="kyc-doc-preview" src={fileUrl(form.id_doc_path)} alt="ID preview" className="h-11 w-16 border border-slate-200 object-cover" />}
            </div>
          </Field>
          <div className="md:col-span-2 mt-2"><p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#6D28D9]/90">3 · Demat account (where shares will be transferred)</p></div>
          <Field label="16-digit demat account number (CDSL BO ID) or NSDL ID (IN + 14 digits)"><input data-testid="kyc-demat-input" required value={form.demat_id} onChange={set("demat_id")} placeholder="1208160001234567" maxLength={20} className={`${inputCls} font-mono2 uppercase`} /></Field>
          <p className="self-end text-xs text-slate-500 md:pb-3">Find it on your broker app or CMR copy. CDSL = 16 digits (DP ID + Client ID). NSDL = IN followed by 14 digits.</p>
          <div className="md:col-span-2 flex flex-wrap items-center justify-between gap-4">
            <p className="max-w-lg font-mono2 text-[9px] uppercase leading-relaxed tracking-[0.15em] text-slate-400">Documents are stored privately and visible only to you and the desk.</p>
            <button data-testid="kyc-submit-button" type="submit" disabled={saving || !!uploading} className="flex items-center gap-2 bg-[#6D28D9] px-7 py-3.5 font-mono2 text-xs font-semibold uppercase tracking-[0.2em] text-[#FFFFFF] transition-colors hover:bg-[#5B21B6] disabled:opacity-50">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShieldCheck className="h-3.5 w-3.5" /> Submit KYC</>}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};
